// Core model builders for FinList, aligned with docs/FINLIST_MODEL_AND_GUARDRAILS.md
// All records are user-scoped (users/{uid}/...). Optional fields remain optional.
// Status fields are strictly controlled (no ad-hoc values).

import { serverTimestamp } from 'firebase/firestore';

// Remove undefined fields so Firestore writes don't fail on undefined values.
const cleanUndefined = (obj) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === undefined) {
      delete obj[key];
    }
  });
  return obj;
};

// Allowed status enums (guardrail: do not invent new statuses)
export const SHOPPING_ITEM_STATUSES = ['pending', 'bought'];
export const TODO_STATUSES = ['todo', 'in_progress', 'done', 'archived'];

// ----- Income -----
// Shape:
// {
//   id, ownerId, source, amount, currency, date,
//   period: 'weekly' | 'monthly' | 'yearly' | 'one_off',
//   category: 'salary' | 'business' | 'gift' | 'investment' | 'other',
//   notes?, createdAt, updatedAt
// }
export const createIncome = (uid, payload = {}) =>
  cleanUndefined({
    ownerId: uid,
    source: payload.source || '',
    amount: Number(payload.amount) || 0,
    currency: payload.currency || 'USD',
    date: payload.date || serverTimestamp(),
    period: payload.period || 'monthly',
    category: payload.category || 'other',
    notes: payload.notes || undefined,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

// ----- Expense -----
// Shape:
// {
//   id, ownerId, description, amount, currency, date,
//   category, subcategory?, source: 'manual' | 'shopping_list', shoppingItemId?,
//   paymentMethod?, isPlanned, createdAt, updatedAt
// }
export const createExpense = (uid, payload = {}) =>
  cleanUndefined({
    ownerId: uid,
    description: payload.description || '',
    amount: Number(payload.amount) || 0,
    currency: payload.currency || 'USD',
    date: payload.date || serverTimestamp(),
    category: payload.category || 'other',
    subcategory: payload.subcategory || undefined,
    source: payload.source || 'manual',
    shoppingItemId: payload.shoppingItemId || undefined,
    paymentMethod: payload.paymentMethod || undefined,
    isPlanned: payload.isPlanned ?? false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

// Build an expense from a shopping item when it is purchased (explicit user action required).
export const createExpenseFromShoppingItem = (uid, shoppingItem, overrides = {}) =>
  cleanUndefined({
    ownerId: uid,
    description: overrides.description || shoppingItem.name || '',
    amount:
      Number(overrides.amount ?? (shoppingItem.boughtUnitPrice || 0) * (shoppingItem.quantity || 1)) || 0,
    currency: overrides.currency || 'USD',
    date: overrides.date || shoppingItem.boughtAt || serverTimestamp(),
    category: overrides.category || shoppingItem.category || 'other',
    subcategory: overrides.subcategory || undefined,
    source: 'shopping_list',
    shoppingItemId: overrides.shoppingItemId || shoppingItem.id,
    paymentMethod: overrides.paymentMethod || undefined,
    isPlanned: overrides.isPlanned ?? false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

// ----- Shopping Lists -----
// shoppingList shape: { id, ownerId, title, createdAt, updatedAt, archived }
export const createShoppingList = (uid, payload = {}) =>
  cleanUndefined({
    ownerId: uid,
    title: payload.title || 'Shopping List',
    archived: payload.archived ?? false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

// shoppingItem shape: see spec; status strictly 'pending' | 'bought'
export const createShoppingItem = (uid, listId, payload = {}) =>
  cleanUndefined({
    ownerId: uid,
    listId,
    name: payload.name || '',
    quantity: Number(payload.quantity) || 1,
    unit: payload.unit || 'unit',
    plannedUnitPrice: payload.plannedUnitPrice !== undefined ? Number(payload.plannedUnitPrice) : undefined,
    category: payload.category || 'other',
    status: SHOPPING_ITEM_STATUSES.includes(payload.status) ? payload.status : 'pending',
    boughtUnitPrice: payload.boughtUnitPrice !== undefined ? Number(payload.boughtUnitPrice) : undefined,
    boughtAt: payload.boughtAt || undefined,
    linkedExpenseId: payload.linkedExpenseId || undefined,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

// ----- Savings Goals & Transactions -----
// savingsGoal shape per spec
export const createSavingsGoal = (uid, payload = {}) =>
  cleanUndefined({
    ownerId: uid,
    name: payload.name || '',
    targetAmount: Number(payload.targetAmount) || 0,
    currency: payload.currency || 'USD',
    currentSaved: Number(payload.currentSaved) || 0,
    deadline: payload.deadline || undefined,
    status: payload.status || 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

// savingsTransaction shape: deposits/withdrawals tied to a goal
export const createSavingsTransaction = (uid, goalId, payload = {}) =>
  cleanUndefined({
    ownerId: uid,
    goalId,
    type: payload.type || 'deposit',
    amount: Number(payload.amount) || 0,
    date: payload.date || serverTimestamp(),
    source: payload.source || 'manual', // e.g., income_allocation | manual
    createdAt: serverTimestamp(),
  });

// ----- Budgets (weekly / monthly / yearly) -----
// budgetPeriod shape per spec
export const createBudgetPeriod = (uid, payload = {}) =>
  cleanUndefined({
    ownerId: uid,
    id: payload.id || '', // caller should set period id like '2025-11' or '2025-W45'
    type: payload.type || 'monthly',
    startDate: payload.startDate || serverTimestamp(),
    endDate: payload.endDate || serverTimestamp(),
    plannedIncome: Number(payload.plannedIncome) || 0,
    plannedSavings: Number(payload.plannedSavings) || 0,
    plannedExpenses: Number(payload.plannedExpenses) || 0,
    actualIncome: Number(payload.actualIncome) || 0,
    actualSavings: Number(payload.actualSavings) || 0,
    actualExpenses: Number(payload.actualExpenses) || 0,
    categoryPlanned: payload.categoryPlanned || undefined,
    categoryActual: payload.categoryActual || undefined,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

// ----- To-Dos (financial tasks) -----
// todo shape per spec; status strictly enforced
export const createTodo = (uid, payload = {}) =>
  cleanUndefined({
    ownerId: uid,
    title: payload.title || '',
    description: payload.description || undefined,
    status: TODO_STATUSES.includes(payload.status) ? payload.status : 'todo',
    dueDate: payload.dueDate || undefined,
    priority: payload.priority || 'medium',
    isFinancialTask: payload.isFinancialTask ?? true,
    estimatedAmount: payload.estimatedAmount !== undefined ? Number(payload.estimatedAmount) : undefined,
    relatedType: payload.relatedType || 'none',
    relatedRef: payload.relatedRef || undefined, // read-only link to another record
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    completedAt: payload.completedAt || undefined,
  });

// ----- Insights Snapshot (optional aggregated store) -----
export const createInsightSnapshot = (uid, payload = {}) =>
  cleanUndefined({
    ownerId: uid,
    id: payload.id || '', // align with budgetPeriod id
    type: payload.type || 'monthly',
    totalIncome: Number(payload.totalIncome) || 0,
    totalExpenses: Number(payload.totalExpenses) || 0,
    totalSavings: Number(payload.totalSavings) || 0,
    savingsRate: Number(payload.savingsRate) || 0,
    overspendingCategories: payload.overspendingCategories || [],
    underspendingCategories: payload.underspendingCategories || [],
    topSpendingCategory: payload.topSpendingCategory || undefined,
    topMerchant: payload.topMerchant || undefined,
    budgetStatus: payload.budgetStatus || 'on_track',
    financialHealthScore: Number(payload.financialHealthScore) || 0,
    riskLevel: payload.riskLevel || 'low',
    summaryText: payload.summaryText || undefined,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

// Notes on guardrails:
// - No bank/external integrations here; all data is user-entered or derived from in-app actions.
// - Status fields are constrained to allowed values above.
// - Cross-module links (e.g., shoppingItemId, relatedRef) are read-only references; they do not move money.
// - Optional fields stay optional; basic flows work without them.
